import { effect, inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { isUndefined, stringOrArrayToArray, stringToBoolean } from 'app/helpers';
import { ConsentService } from 'app/services/consent';
import { SuperadminService } from 'app/services/superadmin';
import { environment } from 'environments/environment';

const GA_MEASUREMENT_ID = 'G-LWYL37SHPN';
const GTM_MEASUREMENT_ID = 'GTM-PCTG2DX';

// GTM/GA attach themselves to the global window object with no bundled types
type TrackingWindow = Window & {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    'ga-disable-G-LWYL37SHPN'?: boolean;
};

@Injectable()
export class TrackingService {
    private superadminService = inject(SuperadminService);
    private consentService = inject(ConsentService);
    private router = inject(Router);

    private trackingAllowed = signal(false);

    // Created as class fields (constructor injection context) rather than inline, since
    // toObservable()/toSignal() must run in an injection context (NG0203 otherwise).
    private trackingAllowed$ = toObservable(this.trackingAllowed);
    // initialValue: null replicates the old combineLatest's startWith(null) — the effect
    // below must react on construction, before the first real NavigationEnd fires.
    private lastNavigationEnd = toSignal(this.router.events.pipe(filter((event) => event instanceof NavigationEnd)), {
        initialValue: null,
    });

    private static gtmLoaded = false;
    private static gaLoaded = false;

    private static envAllowsTracking(currentLocation: string = location.href): boolean {
        const disableTracking = environment.disableTracking as boolean | string | string[] | undefined;

        if (isUndefined(disableTracking)) {
            return true;
        }

        if (typeof disableTracking === 'boolean') {
            return !disableTracking;
        }

        if (disableTracking === 'true' || disableTracking === 'false') {
            return stringToBoolean(disableTracking);
        }

        const disableTrackingUrls = stringOrArrayToArray(disableTracking);

        for (const location of disableTrackingUrls) {
            const regex = new RegExp(location, 'g');

            if (regex.test(currentLocation)) {
                return false;
            }
        }

        return true;
    }

    private loadGtm(): void {
        if (TrackingService.gtmLoaded) {
            return;
        }
        TrackingService.gtmLoaded = true;
        this.consentService.registerLoadedScript('statistics', 'marketing');

        const w = window as TrackingWindow;
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_MEASUREMENT_ID}`;

        document.head.appendChild(script);
    }

    private loadGa(): void {
        if (TrackingService.gaLoaded) {
            return;
        }
        TrackingService.gaLoaded = true;
        this.consentService.registerLoadedScript('statistics');

        const w = window as TrackingWindow;
        w.dataLayer = w.dataLayer || [];

        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        script.onload = () => {
            w.gtag('js', new Date());
            w.gtag('config', GA_MEASUREMENT_ID);
        };
        document.head.appendChild(script);
    }

    constructor() {
        effect(() => {
            const superadmin = this.superadminService.superadmin();
            const statisticsConsent = this.consentService.statistics();
            const marketingConsent = this.consentService.marketing();
            // Read (not used) purely to make the effect re-run on every NavigationEnd,
            // matching the old combineLatest's dependency on router.events.
            this.lastNavigationEnd();

            // Superadmins should not be tracked
            if (superadmin) {
                (window as TrackingWindow)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
                this.trackingAllowed.set(false);

                // In case superadmin is gone, we check if env denys tracking
            } else {
                const envAllowed = TrackingService.envAllowsTracking();
                const gaAllowed = envAllowed && statisticsConsent;
                const gtmAllowed = envAllowed && statisticsConsent && marketingConsent;

                (window as TrackingWindow)[`ga-disable-${GA_MEASUREMENT_ID}`] = !gaAllowed;

                if (gaAllowed) {
                    this.loadGa();
                }
                if (gtmAllowed) {
                    this.loadGtm();
                }
                this.trackingAllowed.set(gaAllowed);
            }
        });
    }

    isTrackingAllowed(): Observable<boolean> {
        return this.trackingAllowed$;
    }
}
